<template>
  <div class="home-page">
    <!-- Header -->
    <header class="home-header">
      <div class="home-header-inner">
        <router-link to="/" class="home-header-logo">
          <img src="@/assets/images/logo-inviplay.svg" alt="Inviplay" />
        </router-link>
        <div class="home-header-actions">
          <template v-if="isAuthenticated">
            <router-link to="/profile-own" class="home-btn-primary">
              {{ $t('home.header.myProfile') }}
            </router-link>
          </template>
          <template v-else>
            <router-link to="/register-preferences" class="home-btn-primary">
              {{ $t('home.header.signup') }}
            </router-link>
            <router-link to="/login" class="home-btn-secondary">
              {{ $t('home.header.login') }}
            </router-link>
          </template>
        </div>
      </div>
    </header>

    <!-- Hero -->
    <section class="home-hero">
      <div class="home-hero-inner">
        <h1 class="home-hero-title">{{ $t('home.hero.title') }}</h1>
        <p class="home-hero-subtitle">{{ $t('home.hero.subtitle') }}</p>
        <router-link
          to="/events"
          class="home-btn-primary home-hero-cta"
          @click.native="trackSection('hero')"
        >
          {{ $t('home.hero.cta') }}
        </router-link>
        <div class="home-store-badges">
          <a
            href="https://apps.apple.com/nl/app/inviplay-invite-play/id1605262654"
            target="_blank"
            rel="noopener"
          >
            <img
              src="@/assets/images/Appstore-Download.svg"
              alt="Download in de App Store"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=nl.inviplay.app.prd"
            target="_blank"
            rel="noopener"
          >
            <img
              src="@/assets/images/Playstore-Download.svg"
              alt="Ontdek het op Google Play"
            />
          </a>
        </div>
      </div>
    </section>

    <!-- Aankomende activiteiten -->
    <home-section
      v-if="events.length"
      :title="$t('home.events.title')"
      link-to="/events"
      :link-text="$t('home.events.viewAll')"
      @link-click="trackSection('events')"
    >
      <div class="home-grid-3">
        <home-event-card
          v-for="event in events"
          :key="event.dateId"
          :name="event.name"
          :activity-name="event.activity_type && event.activity_type.name"
          :image-url="event.imageUrl"
          :date="event.date"
          :start-time="event.startTime"
          :location-name="event.location && event.location.name"
          :city="event.location && event.location.city"
          :available-slots="event.availableSlots"
          :maximum-participants="event.maximumParticipants"
          :cost="event.costFeeIncluded"
          :date-id="event.dateId"
          :security-hash="event.securityHash"
          @select="onEventClick(event)"
        />
      </div>
    </home-section>

    <!-- Sportlocatie huren -->
    <home-section
      :title="$t('home.locations.title')"
      link-to="/locations"
      :link-text="$t('home.locations.viewAll')"
      tinted
      @link-click="trackSection('locations')"
    >
      <div v-if="featuredLocations.length" class="home-grid-3">
        <home-location-card
          v-for="location in featuredLocations"
          :key="location.id"
          :name="location.name"
          :city="location.address && location.address.city"
          :image-url="location.imageUrl"
          :slug="location.slug"
          :lowest-price="location.lowestNonMemberPrice"
          @select="onLocationClick(location)"
        />
      </div>
    </home-section>

    <!-- Groepen -->
    <home-section
      v-if="groups.length"
      :title="$t('home.groups.title')"
      link-to="/groups"
      :link-text="$t('home.groups.viewAll')"
      @link-click="trackSection('groups')"
    >
      <div class="home-grid-3">
        <home-group-card
          v-for="group in groups"
          :key="group.groupId"
          :group-id="group.groupId"
          :name="group.name"
          :city="group.city"
          :member-count="group.memberCount"
          :image-url="group.imageUrl"
          @select="onGroupClick(group)"
        />
      </div>
    </home-section>

    <!-- Steden -->
    <section v-if="visibleCities.length" class="home-cities">
      <div class="home-cities-inner">
        <h2 class="home-cities-title">{{ $t('home.cities.title') }}</h2>
        <div class="home-cities-chips">
          <router-link
            v-for="city in visibleCities"
            :key="city.path"
            :to="city.path"
            class="home-city-chip"
            @click.native="trackSection(`city_${city.name.toLowerCase()}`)"
          >
            {{ city.name }}
          </router-link>
        </div>
      </div>
    </section>

    <!-- Hoe werkt Inviplay -->
    <section class="home-how">
      <div class="home-how-inner">
        <h2 class="home-how-title">{{ $t('home.how.title') }}</h2>
        <div class="home-how-steps">
          <div v-for="step in 4" :key="step" class="home-how-step">
            <span class="home-how-step-number">{{ step }}</span>
            <p class="home-how-step-text">{{ $t(`home.how.step${step}`) }}</p>
          </div>
        </div>
        <p class="home-how-clubs">{{ $t('home.how.clubs') }}</p>
      </div>
    </section>

    <!-- App-CTA banner -->
    <section class="home-app-banner">
      <div class="home-app-banner-inner">
        <h2 class="home-app-banner-title">{{ $t('home.app.title') }}</h2>
        <p class="home-app-banner-text">{{ $t('home.app.text') }}</p>
        <div class="home-store-badges">
          <a
            href="https://apps.apple.com/nl/app/inviplay-invite-play/id1605262654"
            target="_blank"
            rel="noopener"
          >
            <img
              src="@/assets/images/Appstore-Download.svg"
              alt="Download in de App Store"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=nl.inviplay.app.prd"
            target="_blank"
            rel="noopener"
          >
            <img
              src="@/assets/images/Playstore-Download.svg"
              alt="Ontdek het op Google Play"
            />
          </a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="home-footer">
      <div class="home-footer-inner">
        <div class="home-footer-columns">
          <div
            v-for="column in footerColumns"
            :key="column.key"
            class="home-footer-column"
          >
            <h3 class="home-footer-heading">
              {{ $t(`home.footer.${column.key}`) }}
            </h3>
            <ul class="home-footer-links">
              <li v-for="link in column.links" :key="link.path">
                <router-link :to="link.path">{{ link.label }}</router-link>
              </li>
            </ul>
          </div>
        </div>
        <div class="home-footer-bottom">
          <img src="@/assets/images/logo-inviplay.svg" alt="Inviplay" />
          <span>{{ $t('home.footer.tagline') }}</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import HomeSection from '@/components/home/HomeSection'
import HomeEventCard from '@/components/home/HomeEventCard'
import HomeLocationCard from '@/components/home/HomeLocationCard'
import HomeGroupCard from '@/components/home/HomeGroupCard'
import { DATE_UPCOMING, DATE_UPCOMING_RESET } from '@/store/actions/date'
import { GROUP_EXPLORE, GROUP_EXPLORE_RESET } from '@/store/actions/group'
import { LOCATION_GET_COMMERCIAL } from '@/store/actions/location'
import { getImageUrl, formatDate } from '@/utils/formatters'
import analytics from '@/utils/analytics'
import config from '@/utils/config'

export default {
  name: 'HomePage',
  components: {
    HomeSection,
    HomeEventCard,
    HomeLocationCard,
    HomeGroupCard
  },
  data: () => ({
    events: [],
    groups: [],
    eventCities: [],
    // Gebruikt voor de footer-kolom "Huren": die linkt altijd naar alle
    // marketingpagina's, ook als er (nog) geen boekbare locatie voor is.
    rentalTiles: [
      { key: 'tennis', path: '/tennisbaan-huren' },
      { key: 'padel', path: '/padelbaan-huren' },
      { key: 'squash', path: '/squashbaan-huren' },
      { key: 'gym', path: '/gymzaal-huren' },
      { key: 'sportzaal', path: '/sportzaal-huren' },
      { key: 'sporthal', path: '/sporthal-huren' }
    ],
    cities: [
      { name: 'Amsterdam', path: '/amsterdam' },
      { name: 'Rotterdam', path: '/rotterdam' },
      { name: 'Utrecht', path: '/utrecht' },
      { name: 'Arnhem', path: '/arnhem' },
      { name: 'Ede', path: '/ede' },
      { name: 'Amersfoort', path: '/amersfoort' }
    ]
  }),
  computed: {
    ...mapGetters(['isAuthenticated']),
    ...mapState({
      commercialLocations: (state) => state.location.commercial
    }),
    featuredLocations() {
      return this.commercialLocations
        .filter((location) => location.slug)
        .slice(0, 3)
    },
    visibleCities() {
      // Een stad heeft aanbod als er events zijn (meta uit event/upcoming)
      // of een boekbare locatie gevestigd is.
      const withOffer = new Set(
        [
          ...this.eventCities,
          ...this.commercialLocations.map(
            (location) => location.address && location.address.city
          )
        ]
          .filter(Boolean)
          .map((city) => city.toLowerCase())
      )
      // Geen data (nog niet geladen of API-fout): toon alle steden, zodat de
      // city-links — ook in de prerender — nooit wegvallen.
      if (!withOffer.size) return this.cities
      return this.cities.filter((city) =>
        withOffer.has(city.name.toLowerCase())
      )
    },
    footerColumns() {
      return [
        {
          key: 'sports',
          links: [
            {
              label: 'Voetbal',
              path: '/met-een-groepje-voetballen-in-de-buurt'
            },
            {
              label: 'Basketbal',
              path: '/met-een-groepje-basketballen-pickup-games-in-de-buurt'
            },
            {
              label: 'Zaalvoetbal',
              path: '/met-een-groepje-zaalvoetballen-in-de-buurt'
            },
            {
              label: 'Badminton',
              path: '/met-een-groepje-badmintonnen-in-de-buurt'
            },
            {
              label: 'Hardlopen',
              path: '/met-een-groepje-hardlopen-in-de-buurt'
            },
            { label: 'Baanzwemmen', path: '/baanzwemmen-in-de-buurt' }
          ]
        },
        {
          key: 'cities',
          links: this.cities.map((city) => ({
            label: city.name,
            path: city.path
          }))
        },
        {
          key: 'rentals',
          links: this.rentalTiles.map((tile) => ({
            label: this.$t(`home.locations.tiles.${tile.key}`),
            path: tile.path
          }))
        },
        {
          key: 'about',
          links: [
            { label: this.$t('home.footer.allActivities'), path: '/events' },
            { label: this.$t('home.footer.allLocations'), path: '/locations' },
            { label: this.$t('home.footer.allGroups'), path: '/groups' },
            { label: this.$t('home.footer.privacy'), path: '/privacy-policy' },
            {
              label: this.$t('home.footer.terms'),
              path: '/terms-and-Conditions'
            },
            {
              label: this.$t('home.footer.acceptableUse'),
              path: '/acceptable-use-policy'
            }
          ]
        }
      ]
    }
  },
  created: function () {
    this.fetchEvents()
    this.fetchGroups()
    this.$store.dispatch(LOCATION_GET_COMMERCIAL)
  },
  methods: {
    fetchEvents: async function () {
      const upcoming = await this.$store.dispatch(DATE_UPCOMING, {
        skip: 0,
        limit: 6,
        city: '',
        activity: ''
      })
      this.events = (upcoming || []).map((event) => ({
        ...event,
        imageUrl:
          event.image && Object.keys(event.image).length
            ? getImageUrl(event.image)
            : null,
        date: formatDate.short(event.startDateTime),
        startTime: formatDate.time(event.startDateTime)
      }))
      // De response-meta bevat per stad het aantal events; bewaar die vóór
      // de reset zodat de steden-sectie op echt aanbod kan filteren.
      const metaCities =
        (this.$store.state.date &&
          this.$store.state.date.upcoming &&
          this.$store.state.date.upcoming.cities) ||
        []
      this.eventCities = metaCities
        .filter((city) => city.count > 0)
        .map((city) => city.city)
      // De homepage leest uit de action-return; reset de gedeelde
      // upcoming-state zodat /events met een schone lijst en skip=0 start.
      this.$store.commit(DATE_UPCOMING_RESET)
    },
    fetchGroups: async function () {
      const groups = await this.$store.dispatch(GROUP_EXPLORE, {
        skip: 0,
        limit: 3,
        city: '',
        activity: ''
      })
      // De API geeft imageUrl relatief terug (/api/attachments/…); die hoort
      // bij de API-host, niet bij de site zelf.
      this.groups = (groups || []).map((group) => ({
        ...group,
        imageUrl: group.imageUrl
          ? group.imageUrl.startsWith('http')
            ? group.imageUrl
            : `${config.baseUrl}${group.imageUrl}`
          : getImageUrl(group.image, false)
      }))
      this.$store.commit(GROUP_EXPLORE_RESET)
    },
    trackSection: function (section) {
      analytics.capture('homepage_section_clicked', { section })
    },
    onEventClick: function (event) {
      this.trackSection('events')
      if (event.dateId && event.securityHash) {
        this.$router.push(
          `/events/${event.dateId}/${event.securityHash}?origin=home`
        )
      } else {
        this.$router.push('/events')
      }
    },
    onLocationClick: function (location) {
      this.trackSection('locations')
      this.$router.push(
        location.slug ? `/locations/${location.slug}` : '/locations'
      )
    },
    onGroupClick: function (group) {
      this.trackSection('groups')
      this.$router.push(`/groups/${group.groupId}`)
    }
  },
  metaInfo() {
    const title = this.$t('home.meta.title')
    const description = this.$t('home.meta.description')
    // Vaste productie-URL: de homepage-canonical mag nooit met de
    // (per-omgeving wisselende) VUE_APP_FULL_APP_URL meebewegen.
    const url = 'https://www.inviplay.nl/'
    return {
      title,
      meta: [
        { vmid: 'description', name: 'description', content: description },
        { vmid: 'og:title', property: 'og:title', content: title },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: description
        },
        { vmid: 'og:url', property: 'og:url', content: url }
      ],
      link: [{ vmid: 'canonical', rel: 'canonical', href: url }],
      script: [
        {
          vmid: 'ld-organization',
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Inviplay',
            url: 'https://www.inviplay.nl',
            logo:
              'https://www.inviplay.nl/img/icons/android-chrome-512x512.png',
            sameAs: [
              'https://apps.apple.com/nl/app/inviplay-invite-play/id1605262654',
              'https://play.google.com/store/apps/details?id=nl.inviplay.app.prd'
            ]
          }
        },
        {
          vmid: 'ld-website',
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Inviplay',
            url: 'https://www.inviplay.nl'
          }
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.home-page {
  color: $ipBlack;
  background: #fff;
}

/* ---------- Buttons ---------- */
.home-btn-primary {
  display: inline-block;
  padding: 0.5625rem 1.375rem;
  border-radius: 10px;
  background: $ipGreen;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s;

  &:hover {
    background: darken($ipGreen, 6%);
    color: #fff;
    text-decoration: none;
  }
}

.home-btn-secondary {
  display: inline-block;
  padding: 0.5rem 1.375rem;
  border-radius: 10px;
  border: 1.5px solid $ipGreen;
  color: $ipGreen;
  font-size: 0.9rem;
  font-weight: 600;
  background: #fff;
  text-decoration: none;
  transition: background 0.15s;

  &:hover {
    background: #e8f6f1;
    color: $ipGreen;
    text-decoration: none;
  }
}

/* ---------- Header ---------- */
.home-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid #ececec;
}

.home-header-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 1.5rem;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  @media (max-width: 359px) {
    padding: 0 1rem;
  }

  @media (min-width: 768px) {
    height: 72px;
  }
}

.home-header-logo {
  flex-shrink: 0;
}

.home-header-logo img {
  display: block;
  height: 26px;
  width: auto;

  @media (max-width: 359px) {
    height: 20px;
  }

  @media (min-width: 480px) {
    height: 32px;
  }

  @media (min-width: 768px) {
    height: 40px;
  }
}

.home-header-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;

  // Narrow phones: slimmer buttons leave room for the logo at its own ratio
  @media (max-width: 479px) {
    .home-btn-primary,
    .home-btn-secondary {
      padding-left: 0.875rem;
      padding-right: 0.875rem;

      @media (max-width: 359px) {
        padding-left: 0.625rem;
        padding-right: 0.625rem;
      }
    }
  }
}

/* ---------- Hero ---------- */
.home-hero {
  background: #f7f9f8;
  border-bottom: 1px solid #efefef;
}

.home-hero-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 3rem 1.5rem 3.25rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 3.5rem 1.5rem 3.25rem;
  }
}

.home-hero-title {
  margin: 0 auto;
  max-width: 760px;
  font-size: 1.875rem;
  line-height: 1.2;
  font-weight: 600;
  color: $ipBlack;

  @media (min-width: 768px) {
    font-size: 2.75rem;
  }
}

.home-hero-subtitle {
  margin: 1.125rem auto 0;
  max-width: 620px;
  font-size: 1rem;
  line-height: 1.65;
  color: $ipGreyDark;

  @media (min-width: 768px) {
    font-size: 1.0625rem;
  }
}

.home-hero-cta {
  margin-top: 1.75rem;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  box-shadow: 0 4px 14px rgba(0, 155, 109, 0.25);
}

.home-store-badges {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
  gap: 0.625rem;

  img {
    height: 42px;
  }
}

/* ---------- Grids ---------- */
.home-grid-3 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }
}

/* ---------- Steden ---------- */
.home-cities {
  border-top: 1px solid #efefef;
}

.home-cities-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.home-cities-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: $ipBlack;
}

.home-cities-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.home-city-chip {
  padding: 0.5rem 1.125rem;
  border: 1px solid #e0e0e0;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  color: $ipBlack;
  text-decoration: none;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: $ipGreen;
    color: $ipGreen;
    text-decoration: none;
  }
}

/* ---------- Hoe werkt het ---------- */
.home-how {
  border-top: 1px solid #efefef;
}

.home-how-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 3.5rem 1.5rem;
  text-align: center;
}

.home-how-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: $ipBlack;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
}

.home-how-steps {
  margin-top: 2.25rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.home-how-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.home-how-step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e8f6f1;
  color: $ipGreen;
  font-weight: 700;
}

.home-how-step-text {
  margin: 0;
  max-width: 220px;
  font-size: 0.9rem;
  color: $ipBlack;
}

.home-how-clubs {
  margin: 2.25rem 0 0;
  font-size: 0.9rem;
  color: $ipGreyDark;
}

/* ---------- App banner ---------- */
.home-app-banner {
  background: $ipGreen;
  color: #fff;
}

.home-app-banner-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 3.25rem 1.5rem;
  text-align: center;
}

.home-app-banner-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;

  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
}

.home-app-banner-text {
  margin: 0.75rem auto 0;
  max-width: 540px;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
}

/* ---------- Footer ---------- */
.home-footer {
  background: #fafafa;
  border-top: 1px solid #ececec;
}

.home-footer-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 3rem 1.5rem 2rem;
}

.home-footer-columns {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.home-footer-heading {
  margin: 0 0 0.875rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: $ipBlack;
}

.home-footer-links {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin-bottom: 0.5rem;
  }

  a {
    font-size: 0.84rem;
    color: $ipGreyDark;
    text-decoration: none;

    &:hover {
      color: $ipGreen;
      text-decoration: none;
    }
  }
}

.home-footer-bottom {
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #ececec;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  img {
    height: 28px;
    width: auto;
  }

  span {
    font-size: 0.84rem;
    color: $ipGreyLight;
  }
}
</style>
